import { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { MapPin, Users, Ticket, DollarSign, Plus, Pencil, Trash2, UserPlus, Layers, Clock, ArrowUp, Wallet, Images, FileText } from 'lucide-react';
import BackButton from '../../../components/BackButton';
import { getEvent, removeAttendee, setAttendeeStatus, deletePhase, setBoletajeConfig, EventDetail, EventPhase, Attendee, money, fmtEventDate } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import EventForm from '../components/EventForm';
import AttendeeForm from '../components/AttendeeForm';
import PhaseForm from '../components/PhaseForm';
import FestivalFinanzas from '../components/FestivalFinanzas';
import FestivalBoletaje from '../components/FestivalBoletaje';
import EventGallery from '../components/EventGallery';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';

// Mismo lenguaje visual que las fichas de alumno/profesor (Carlos 15-ago): cabecera de sección con
// negritas, color de texto normal (no gris), icono de marca y una línea que la separa del contenido.
// `h3Txt` es la variante sin línea, para cuando la cabecera comparte renglón con un botón (`h3Row`
// se lleva entonces la línea, de lado a lado y no solo bajo el título).
const h3Txt = 'flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content';
const h3Cls = `mb-2 border-b border-line pb-1.5 ${h3Txt}`;
const h3Row = 'mb-2 flex items-center justify-between gap-2 border-b border-line pb-1.5';

function Kpi({ icon: Icon, label, value, sub }: { icon: typeof Users; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-content-3"><Icon size={13} /> {label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-content">{value}</div>
      {sub && <div className="mt-1 text-xs text-content-3">{sub}</div>}
    </div>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const eventId = Number(id);
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  const [d, setD] = useState<EventDetail | null>(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [phaseEdit, setPhaseEdit] = useState<EventPhase | null | undefined>(undefined); // undefined=cerrado, null=nueva
  // ?tab=finanzas&sub=presupuesto|movimientos|comparativo abre directo una pestaña (enlaces y capturas).
  const [qs] = useSearchParams();
  const tabQs = qs.get('tab');
  const [tab, setTab] = useState<'boletaje' | 'finanzas' | 'galeria'>(tabQs === 'finanzas' || tabQs === 'galeria' ? tabQs : 'boletaje');
  const subQs = qs.get('sub');
  // Control de boletos del festival (28-oct-2026): el botón «Vender boletos» del hero abre el modal de la pestaña Boletaje.
  const [ventaPedida, setVentaPedida] = useState(0);
  // Acceso por evento (28-oct-2026): quien entra con «equipo vende» puede vender boletos sin ser admin.
  const puedeVender = isAdmin || !!d?.data?.acceso?.puede_operar;

  const load = useCallback(() => getEvent(eventId).then((x) => {
    setD(x);
    // Festival con libreta propia o antecedente: lo útil es su finanza → abre ahí (salvo que el enlace pida otra pestaña).
    // Con control de boletos del festival activo, lo urgente es el boletaje: se queda ahí.
    if (!tabQs && x.data.ticket_mode !== 'festival' && (x.data.fin_mode === 'utilidad' || Number(x.data.is_reference) === 1)) setTab('finanzas');
  }).catch(() => setD(null)), [eventId, tabQs]);
  useEffect(() => { load(); }, [load]);

  async function quitar(accountId: number, name: string) {
    if (!confirm(`¿Quitar a ${name} de la lista?`)) return;
    try { await removeAttendee(eventId, accountId); toast.success('Asistente quitado'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
  }
  async function promover(a: Attendee) {
    try { await setAttendeeStatus(eventId, a.account_id, 'going'); toast.success(`${a.firstname} confirmado`); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo promover'); }
  }
  async function activarFestival() {
    if (!confirm('Se activa el control de boletos del festival: participantes con prioridad, ventas con folio y recibo, lista de espera calculada y caja propia. Los asistentes de hoy se conservan pero dejan de verse aquí. ¿Continuar?')) return;
    try { await setBoletajeConfig(eventId, { ticket_mode: 'festival' }); toast.success('Control de boletos del festival activado'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo activar'); }
  }
  async function borrarFase(p: EventPhase) {
    if (!confirm(`¿Borrar la fase "${p.name}"?`)) return;
    try { await deletePhase(eventId, p.phase_id); toast.success('Fase borrada'); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo borrar'); }
  }

  if (!d) return <div><Skeleton className="h-64 w-full rounded-2xl" /></div>;

  const ev = d.data;
  const festival = ev.ticket_mode === 'festival';
  const color = ev.event_type_color ? `#${ev.event_type_color}` : null;
  const going = d.attendees.filter((a) => a.status !== 'waitlist');
  const waitlist = d.attendees.filter((a) => a.status === 'waitlist');
  const ticketsGoing = d.tickets?.going ?? going.length;
  const ticketsWait = d.tickets?.waitlist ?? 0;
  const occ = ev.capacity ? Math.round((ticketsGoing / ev.capacity) * 100) : null;

  const phaseWindow = (p: EventPhase) => {
    const f = (s: string | null) => (s ? fmtEventDate(s, false) : null);
    const a = f(p.starts_at), b = f(p.ends_at);
    if (a && b) return `${a} → ${b}`;
    if (a) return `desde ${a}`;
    if (b) return `hasta ${b}`;
    return 'sin fechas';
  };

  const AttendeeRow = (a: Attendee, wait: boolean) => (
    <div key={a.account_id} className="flex items-center gap-3 px-4 py-2.5">
      <Link to={`/alumnos/${a.account_id}`}>
        <PersonAvatar contactId={a.account_id} hasPhoto={a.has_photo}
          name={a.firstname} lastName={a.lastname} size="h-8 w-8" />
      </Link>
      <Link to={`/alumnos/${a.account_id}`} className="flex-1 truncate text-sm font-medium text-content hover:underline">
        {a.firstname} {a.lastname}
        {a.phase_name && <span className="ml-1.5 text-xs font-normal text-content-3">· {a.phase_name}</span>}
      </Link>
      {a.qty > 1 && <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent-ink">×{a.qty}</span>}
      {a.source === 'manual'
        ? <span className="rounded-full bg-brand-blue/10 px-2 py-0.5 text-[10px] font-medium text-brand-blue">manual</span>
        : <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] text-content-3">Kydemy</span>}
      {isAdmin && wait && (
        <button onClick={() => promover(a)} className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-600 hover:bg-emerald-500/20" title="Confirmar (dar cupo)"><ArrowUp size={12} /> Confirmar</button>
      )}
      {isAdmin && a.source === 'manual' && (
        <button onClick={() => quitar(a.account_id, `${a.firstname} ${a.lastname}`)} className="text-content-3 hover:text-red-500" title="Quitar"><Trash2 size={14} /></button>
      )}
    </div>
  );

  return (
    // A lo ancho (26-oct-2026): las tablas de Finanzas del festival lo necesitan; el ancho lo gobierna AppLayout.
    <div>
      <BackButton fallback="/eventos" fallbackLabel="eventos" />

      {/* Hero */}
      <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
        <div className="relative h-48 w-full bg-surface-2 sm:h-60">
          {ev.image_url ? (
            <img src={ev.image_url} alt="" className="h-full w-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="h-full w-full" style={{ background: color ? `linear-gradient(135deg, ${color}, rgb(var(--c-blue)))` : 'linear-gradient(135deg, rgb(var(--c-accent)), rgb(var(--c-blue)))' }} />
          )}
          {ev.event_type && (
            <span className="absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold text-white shadow"
              style={{ background: color || 'rgb(var(--c-blue))' }}>{ev.event_type}</span>
          )}
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 p-5">
          <div>
            <h1 className="font-display text-2xl font-bold text-content">{ev.name}</h1>
            <p className="mt-1 capitalize text-content-2">{fmtEventDate(ev.datetime)}</p>
            {ev.venue && <p className="mt-1 flex items-center gap-1 text-sm text-content-3"><MapPin size={14} /> {ev.venue}{ev.venue_address ? ` — ${ev.venue_address}` : ''}</p>}
          </div>
          {(isAdmin || (festival && puedeVender)) && (
            <div className="flex gap-2">
              {isAdmin && <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 hover:bg-surface-2"><Pencil size={14} /> Editar</button>}
              {festival
                ? <button onClick={() => { setTab('boletaje'); setVentaPedida((n) => n + 1); }} className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent hover:opacity-90"><Ticket size={15} /> Vender boletos</button>
                : isAdmin && <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent hover:opacity-90"><UserPlus size={15} /> Boletos</button>}
            </div>
          )}
        </div>
      </div>

      {/* Pestañas del evento */}
      <div className="mt-4 flex gap-1 border-b border-line">
        {([['boletaje', 'Boletaje', Ticket], ['finanzas', 'Finanzas del evento', Wallet], ['galeria', 'Galería', Images]] as const).map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition ${tab === key ? 'border-accent text-content' : 'border-transparent text-content-3 hover:text-content-2'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'finanzas' && <div className="mt-4"><FestivalFinanzas eventId={eventId} isAdmin={isAdmin}
        initialSub={subQs === 'presupuesto' || subQs === 'movimientos' || subQs === 'comparativo' ? subQs : undefined} /></div>}
      {tab === 'galeria' && <div className="mt-4"><EventGallery eventId={eventId} isAdmin={isAdmin} /></div>}

      {/* Modo festival (Mabel 28-oct-2026): marcador, participantes, ventas con recibo, caja del festival. */}
      {tab === 'boletaje' && festival && <div className="mt-4"><FestivalBoletaje eventId={eventId} isAdmin={isAdmin} puedeVender={puedeVender} ventaSolicitada={ventaPedida} onEventoCambiado={load} /></div>}

      {tab === 'boletaje' && !festival && (<>
      {isAdmin && ev.fin_mode === 'utilidad' && Number(ev.is_reference) !== 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-dashed border-line bg-surface px-4 py-2.5 text-xs text-content-3">
          <span>Boletaje simple (asistentes). Para un festival con participantes, lista de espera, recibos y caja propia:</span>
          <button onClick={activarFestival} className="font-medium text-brand-blue hover:underline" data-activar-festival>Activar control de boletos del festival</button>
        </div>
      )}
      {/* KPIs (por BOLETOS) */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi icon={Ticket} label="Boletos vendidos" value={String(ticketsGoing)} sub={ev.capacity ? `de ${ev.capacity} (${occ}%)` : `${going.length} alumnos`} />
        <Kpi icon={DollarSign} label="Precio" value={ev.price != null ? money(ev.price) : '—'} sub="por boleto" />
        <Kpi icon={DollarSign} label="Recaudado" value={money(d.revenue.total)} sub={`${d.revenue.count} pagos`} />
        <Kpi icon={Clock} label="Lista de espera" value={String(ticketsWait)} sub={ev.capacity ? `cupo libre: ${Math.max(0, ev.capacity - ticketsGoing)}` : 'boletos'} />
      </div>

      {/* Fases de boletaje (admin) */}
      {(isAdmin || d.phases.length > 0) && (
        <div className="mt-4">
          <div className={h3Row}>
            <h2 className={h3Txt}><Layers size={13} className="text-accent-ink" /> Fases de venta</h2>
            {isAdmin && <button onClick={() => setPhaseEdit(null)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline"><Plus size={13} /> Nueva fase</button>}
          </div>
          {d.phases.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-surface p-4 text-sm text-content-3">
              Sin fases. Vende con el cupo general del evento, o crea fases (preventa, fase 1…) con su propio cupo y límite de boletos por alumno.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {d.phases.map((p) => {
                const sold = Number(p.sold || 0);
                const pct = p.capacity ? Math.min(100, Math.round((sold / p.capacity) * 100)) : null;
                return (
                  <div key={p.phase_id} className={`rounded-2xl border bg-surface p-4 ${p.active ? 'border-line' : 'border-line/50 opacity-60'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-display font-semibold text-content">{p.name}</span>
                          {!p.active && <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] text-content-3">inactiva</span>}
                        </div>
                        <div className="mt-0.5 text-xs text-content-3">{phaseWindow(p)}</div>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <button onClick={() => setPhaseEdit(p)} className="text-content-3 hover:text-content" title="Editar fase"><Pencil size={13} /></button>
                          <button onClick={() => borrarFase(p)} className="text-content-3 hover:text-red-500" title="Borrar fase"><Trash2 size={13} /></button>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-2">
                      <span>Cupo: <strong className="text-content">{sold}{p.capacity != null ? `/${p.capacity}` : ''}</strong></span>
                      {p.max_per_account != null && <span>Máx/alumno: <strong className="text-content">{p.max_per_account}</strong></span>}
                      <span>Precio: <strong className="text-content">{p.price != null ? money(p.price) : (ev.price != null ? money(ev.price) : '—')}</strong></span>
                    </div>
                    {pct != null && (
                      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
                        <div className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : 'bg-accent'}`} style={{ width: `${pct}%` }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-5">
        {/* Asistentes */}
        <div className="lg:col-span-3 space-y-4">
          <div>
            <div className={h3Row}>
              <h2 className={h3Txt}><Users size={13} className="text-accent-ink" /> Confirmados ({going.length}{ticketsGoing !== going.length ? ` · ${ticketsGoing} boletos` : ''})</h2>
              {isAdmin && <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline"><Plus size={13} /> Agregar</button>}
            </div>
            {going.length === 0 ? (
              <EmptyState icon={Users} title="Sin asistentes aún" subtitle="Registra el primero." compact />
            ) : (
              <div className="divide-y divide-line/60 overflow-hidden rounded-2xl border border-line bg-surface">
                {going.map((a) => AttendeeRow(a, false))}
              </div>
            )}
          </div>

          {waitlist.length > 0 && (
            <div>
              {/* Conserva el ámbar (es la señal de «pendientes»), con el peso del patrón. */}
              <h2 className="mb-2 flex items-center gap-1.5 border-b border-line pb-1.5 text-xs font-bold uppercase tracking-wider text-amber-600"><Clock size={13} /> Lista de espera ({waitlist.length}{ticketsWait !== waitlist.length ? ` · ${ticketsWait} boletos` : ''})</h2>
              <div className="divide-y divide-line/60 overflow-hidden rounded-2xl border border-amber-500/30 bg-amber-500/5">
                {waitlist.map((a) => AttendeeRow(a, true))}
              </div>
            </div>
          )}
        </div>

        {/* Descripción / detalle */}
        <div className="lg:col-span-2">
          <h2 className={h3Cls}><FileText size={13} className="text-accent-ink" /> Detalle</h2>
          <div className="rounded-2xl border border-line bg-surface p-4">
            {ev.description
              ? <div className="prose prose-sm max-w-none text-sm leading-relaxed text-content-2 [&_p]:my-1" dangerouslySetInnerHTML={{ __html: ev.description }} />
              : <p className="text-sm text-content-3">Sin descripción.</p>}
          </div>
        </div>
      </div>
      </>)}

      {editing && <EventForm event={ev} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {adding && <AttendeeForm eventId={eventId} phases={d.phases} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {phaseEdit !== undefined && <PhaseForm eventId={eventId} phase={phaseEdit} onClose={() => setPhaseEdit(undefined)} onSaved={() => { setPhaseEdit(undefined); load(); }} />}
    </div>
  );
}
